/* global globalThis */
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { logoutUser } from "sdk/services/userService";
import { setSubdomain } from "sdk/actions/getSettingsAction";
import useUrlInfo from "./useUrlInfo";

const isDeployedEnv = () =>
  process.env.NODE_ENV === "production" || process.env.NODE_ENV === "staging";

/**
 * Returns a `logout` callback that ends the session: calls the logout API,
 * clears local storage and cookies, resets the subdomain, notifies any
 * React Native WebView host, and redirects (to the backend `returnToUrl` in
 * prod/staging, or to `/login` locally).
 *
 * Previously this exact flow was duplicated across the header menus and the
 * web-not-found screen.
 *
 * @param {{ onSuccess?: () => void }} [options] `onSuccess` runs after the
 *   session is cleared and before the redirect (e.g. to close a drawer).
 */
export default function useLogout({ onSuccess } = {}) {
  const dispatch = useDispatch();
  const { currentHost } = useUrlInfo();

  return useCallback(async () => {
    try {
      const response = await logoutUser();
      if (response?.status === 200) {
        localStorage.clear();
        Object.keys(Cookies.get()).forEach((cookieName) => {
          Cookies.remove(cookieName, {
            path: "/",
            domain: currentHost,
            secure: true,
          });
        });
        dispatch(setSubdomain(null));
        if (typeof onSuccess === "function") {
          onSuccess();
        }
        globalThis.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: "LOGOUT" })
        );
        globalThis.location.href = isDeployedEnv()
          ? response?.data?.returnToUrl
          : "/login";
      } else {
        console.error("Something went wrong during logout.");
      }
    } catch (error) {
      console.error(error?.message);
    }
  }, [dispatch, currentHost, onSuccess]);
}
